/**
 * Membership React Query Hooks
 *
 * React Query hooks for company membership operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type { UpdateMembershipDto } from '@lognai/plugin-admin';
import type { PaginatedListResponse } from '../api/client';
import * as membershipApi from '../api/memberships';
import type { EnrichedMembership, MembershipQueryParams } from '../api/memberships';
import { membershipKeys, queryKeys } from './query-keys';

/**
 * Get list of members for a company
 */
export const useMembers = (
  companyId: string,
  params?: MembershipQueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedListResponse<EnrichedMembership>>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: membershipKeys.list(companyId, params),
    queryFn: () => membershipApi.listMembers(companyId, params),
    enabled: !!companyId,
    ...options,
  });
};

/**
 * Get a single member by ID
 */
export const useMember = (
  companyId: string,
  memberId: string,
  options?: Omit<UseQueryOptions<EnrichedMembership>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: membershipKeys.detail(companyId, memberId),
    queryFn: () => membershipApi.getMember(companyId, memberId),
    enabled: !!companyId && !!memberId,
    ...options,
  });
};

/**
 * Update a member
 */
export const useUpdateMember = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<
      EnrichedMembership,
      Error,
      { memberId: string; dto: UpdateMembershipDto }
    >,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: EnrichedMembership,
      variables: { memberId: string; dto: UpdateMembershipDto },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, dto }) =>
      membershipApi.updateMember(companyId, memberId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: membershipKeys.detail(companyId, variables.memberId),
      });
      queryClient.invalidateQueries({ queryKey: membershipKeys.lists(companyId) });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Remove a member
 */
export const useRemoveMember = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<
      void,
      Error,
      { memberId: string; reason?: string; revokeAccess?: boolean }
    >,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: void,
      variables: { memberId: string; reason?: string; revokeAccess?: boolean },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, reason, revokeAccess }) =>
      membershipApi.removeMember(companyId, memberId, { reason, revokeAccess }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: membershipKeys.lists(companyId) });
      queryClient.removeQueries({
        queryKey: membershipKeys.detail(companyId, variables.memberId),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Remove a user from a company (Company → Members lists users, so the users
 * list and that user's detail are refreshed as well as the membership lists).
 */
export const useRemoveUserFromCompany = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => membershipApi.removeUserFromCompany(companyId, userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
      queryClient.invalidateQueries({ queryKey: membershipKeys.lists(companyId) });
    },
  });
};
